import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  FiActivity,
  FiFilter,
  FiCalendar,
  FiUser,
  FiShield,
  FiDatabase,
  FiCheck,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiClock,
  FiEdit3,
  FiUserPlus,
  FiUserX,
  FiEye,
  FiSettings,
  FiLogIn,
  FiLogOut,
  FiSearch,
  FiXCircle,
} from 'react-icons/fi';

/**
 * AdminAuditLogs - Audit Log Viewer (READ-ONLY)
 * Enterprise Rule: Append-only audit trail - no edit or delete actions
 * Shows: Timeline view, filters, hash integrity status
 */
const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    start_date: '',
    end_date: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  // Action categories for filter dropdown
  const actionCategories = [
    { value: '', label: 'All Actions' },
    // Auth
    { value: 'admin_login', label: 'Admin Login' },
    { value: 'admin_logout', label: 'Admin Logout' },
    // User management
    { value: 'admin_change_user_plan', label: 'Plan Change' },
    { value: 'admin_change_user_role', label: 'Role Change' },
    { value: 'admin_suspend_user', label: 'Suspend User' },
    { value: 'admin_activate_user', label: 'Activate User' },
    { value: 'admin_disable_user', label: 'Disable User' },
    // Maintenance
    { value: 'maintenance_scheduled', label: 'Maintenance Scheduled' },
    { value: 'maintenance_activated_immediately', label: 'Maintenance Activated' },
    { value: 'maintenance_ended', label: 'Maintenance Ended' },
    { value: 'maintenance_cancelled', label: 'Maintenance Cancelled' },
    // Force logout
    { value: 'all_users_force_logout', label: 'Force Logout All' },
    { value: 'user_force_logout', label: 'Force Logout User' },
    // Settings
    { value: 'admin_update_settings', label: 'Settings Changed' },
    { value: 'admin_view_settings', label: 'View Settings' },
    // Security / 2FA
    { value: '2fa_verify_success', label: '2FA Success' },
    { value: '2fa_verify_failed', label: '2FA Failed' },
    { value: '2fa_rate_limited', label: '2FA Rate Limited' },
    // Reads
    { value: 'admin_view_statistics', label: 'View Statistics' },
    { value: 'admin_list_users', label: 'View Users' },
    { value: 'admin_view_audit_logs', label: 'View Audit Logs' },
  ];

  const entityTypes = [
    { value: '', label: 'All Entities' },
    { value: 'user', label: 'User' },
    { value: 'system', label: 'System' },
    { value: 'security', label: 'Security' },
    { value: 'maintenance_schedule', label: 'Maintenance' },
    { value: 'admin_settings', label: 'Settings' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'audit_log', label: 'Audit Log' },
  ];

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...(filters.action && { action: filters.action }),
        ...(filters.entity_type && { entity_type: filters.entity_type }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(debouncedSearch && { search: debouncedSearch }),
      };

      const res = await adminAPI.getAuditLogs(params);
      setLogs(res.data.data.logs || []);
      setTotal(res.data.data.total || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error('Audit logs error:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, debouncedSearch]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      start_date: '',
      end_date: '',
    });
    setSearchTerm('');
    setDebouncedSearch('');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v) || debouncedSearch;

  const totalPages = Math.ceil(total / limit);

  // Get icon for action type
  const getActionIcon = (action) => {
    if (action?.includes('login')) return FiLogIn;
    if (action?.includes('logout')) return FiLogOut;
    if (action?.includes('2fa')) return FiShield;
    if (action?.includes('maintenance')) return FiAlertTriangle;
    if (action?.includes('view') || action?.includes('list')) return FiEye;
    if (action?.includes('change') || action?.includes('update')) return FiEdit3;
    if (action?.includes('suspend') || action?.includes('disable')) return FiUserX;
    if (action?.includes('activate')) return FiUserPlus;
    if (action?.includes('setting')) return FiSettings;
    return FiActivity;
  };

  // Get color for action type
  const getActionColor = (action) => {
    if (action?.includes('suspend') || action?.includes('disable') || action?.includes('delete')) return '#F64E60';
    if (action?.includes('2fa_rate_limited') || action?.includes('2fa_verify_failed')) return '#F64E60';
    if (action?.includes('activate') || action?.includes('create') || action?.includes('2fa_verify_success')) return '#1BC5BD';
    if (action?.includes('maintenance')) return '#FD7E14';
    if (action?.includes('change') || action?.includes('update')) return '#FFA800';
    if (action?.includes('view') || action?.includes('list')) return '#7E8299';
    if (action?.includes('login') || action?.includes('logout')) return '#6C5CE7';
    return '#3699FF';
  };

  // Format action name for display
  const formatAction = (action) => {
    if (!action) return 'Unknown';
    return action
      .replace('admin_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format timestamp for timeline
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check hash integrity status
  const getHashStatus = (log) => {
    // If record_hash exists, consider it valid (actual verification would be server-side)
    if (log.record_hash) {
      return { valid: true, label: 'Verified', icon: FiCheck, color: '#1BC5BD' };
    }
    // No hash = unknown/legacy record
    return { valid: null, label: 'No Hash', icon: FiAlertTriangle, color: '#FFA800' };
  };

  // Get severity for an action
  const getSeverity = (action) => {
    if (!action) return { level: 'low', label: 'Low', color: '#1BC5BD', bg: '#E8FFF3' };
    if (action.includes('2fa_verify_failed') || action.includes('2fa_rate_limited') ||
        action.includes('suspend') || action.includes('disable') || action.includes('force_logout')) {
      return { level: 'high', label: 'High', color: '#F64E60', bg: '#FFF5F8' };
    }
    if (action.includes('maintenance') || action.includes('setting') || action.includes('change') ||
        action.includes('update') || action.includes('activate')) {
      return { level: 'medium', label: 'Medium', color: '#FFA800', bg: '#FFF8DD' };
    }
    return { level: 'low', label: 'Low', color: '#1BC5BD', bg: '#E8FFF3' };
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 className="admin-page-title">
              <FiActivity size={24} style={{ marginRight: 8 }} />
              Activity Logs
            </h1>
            <p className="admin-page-subtitle">
              Admin action audit trail - Read-only, tamper-protected
            </p>
          </div>
          <button
            onClick={fetchLogs}
            className="admin-btn admin-btn-secondary"
            disabled={loading}
          >
            <FiRefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="audit-search-wrapper">
          <FiSearch size={18} className="audit-search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by user email or action..."
            className="admin-input audit-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearchChange('')}
              className="audit-search-clear"
              title="Clear search"
            >
              <FiXCircle size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div
          className="admin-card-header"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowFilters(!showFilters)}
        >
          <h2 className="admin-card-title">
            <FiFilter size={18} />
            Filters
          </h2>
          <span className="admin-card-badge">
            {Object.values(filters).filter(v => v).length} active
          </span>
        </div>

        {showFilters && (
          <div style={{ padding: '16px 0' }}>
            <div className="admin-filter-grid">
              <div className="admin-filter-group">
                <label>Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="admin-select"
                >
                  {actionCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="admin-filter-group">
                <label>Entity Type</label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                  className="admin-select"
                >
                  {entityTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="admin-filter-group">
                <label>
                  <FiCalendar size={14} style={{ marginRight: 4 }} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="admin-input"
                />
              </div>

              <div className="admin-filter-group">
                <label>
                  <FiCalendar size={14} style={{ marginRight: 4 }} />
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="admin-input"
                />
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button onClick={clearFilters} className="admin-btn admin-btn-secondary">
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Audit Log Timeline */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <FiClock size={18} />
            Audit Timeline
          </h2>
          <span className="admin-card-badge">{total} records</span>
        </div>

        {loading ? (
          <div className="admin-loading" style={{ padding: 40 }}>
            <div className="admin-loading-spinner"></div>
            <p>Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#7E8299' }}>
            <FiSearch size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: 4 }}>No activity found</p>
            <p style={{ fontSize: '0.85rem', marginBottom: 20 }}>
              {hasActiveFilters
                ? 'Try adjusting your search or filters to find what you\'re looking for.'
                : 'No audit logs have been recorded yet.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="admin-btn admin-btn-secondary">
                <FiXCircle size={14} style={{ marginRight: 6 }} />
                Reset All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="audit-timeline">
            {logs.map((log, index) => {
              const ActionIcon = getActionIcon(log.action);
              const actionColor = getActionColor(log.action);
              const hashStatus = getHashStatus(log);
              const HashIcon = hashStatus.icon;
              const severity = getSeverity(log.action);

              // Date group separator
              const logDate = new Date(log.created_at).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
              const prevDate = index > 0 ? new Date(logs[index - 1].created_at).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : null;
              const showDateSep = index === 0 || logDate !== prevDate;

              return (
                <React.Fragment key={log.id || index}>
                  {showDateSep && (
                    <div className="audit-date-separator">
                      <FiCalendar size={12} />
                      <span>{logDate}</span>
                    </div>
                  )}
                  <div className="audit-timeline-item">
                    <div className="audit-timeline-marker" style={{ backgroundColor: actionColor }}>
                      <ActionIcon size={14} color="white" />
                    </div>

                    <div className="audit-timeline-content">
                      <div className="audit-timeline-header">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="audit-action" style={{ color: actionColor }}>
                            {formatAction(log.action)}
                          </span>
                          <span className="audit-severity-badge" style={{ color: severity.color, backgroundColor: severity.bg }}>
                            {severity.label}
                          </span>
                        </span>
                        <span className="audit-timestamp">
                          {formatTimestamp(log.created_at)}
                        </span>
                      </div>

                      <div className="audit-details">
                        <div className="audit-detail-row">
                          <FiUser size={14} />
                          <span>Admin: {log.admin_email || 'System'}</span>
                        </div>

                        {log.entity_type && (
                          <div className="audit-detail-row">
                            <FiDatabase size={14} />
                            <span>
                              {log.entity_type}
                              {log.entity_id && `: ${log.entity_id.substring(0, 8)}...`}
                            </span>
                          </div>
                        )}

                        {log.ip_address && (
                          <div className="audit-detail-row">
                            <FiShield size={14} />
                            <span>IP: {log.ip_address}</span>
                          </div>
                        )}
                      </div>

                      {/* Changes (old/new values) */}
                      {(log.old_values || log.new_values) && (
                        <div className="audit-changes">
                          {log.old_values && Object.keys(log.old_values).length > 0 && (
                            <div className="audit-change-block old">
                              <span className="audit-change-label">Previous:</span>
                              <code>{JSON.stringify(log.old_values, null, 0)}</code>
                            </div>
                          )}
                          {log.new_values && Object.keys(log.new_values).length > 0 && (
                            <div className="audit-change-block new">
                              <span className="audit-change-label">New:</span>
                              <code>{JSON.stringify(log.new_values, null, 0)}</code>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hash Integrity Status */}
                      <div className="audit-integrity" style={{ color: hashStatus.color }}>
                        <HashIcon size={12} />
                        <span>{hashStatus.label}</span>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-btn admin-btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <FiChevronLeft size={16} />
              Previous
            </button>
            <span className="admin-pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="admin-btn admin-btn-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
              <FiChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Inline Styles for Audit Timeline */}
      <style>{`
        .admin-filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        .admin-filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .admin-filter-group label {
          font-size: 0.85rem;
          color: #7E8299;
          display: flex;
          align-items: center;
        }
        .admin-select, .admin-input {
          padding: 10px 12px;
          border: 1px solid #E4E6EF;
          border-radius: 6px;
          font-size: 0.9rem;
          background: white;
        }
        .admin-select:focus, .admin-input:focus {
          outline: none;
          border-color: #6C5CE7;
        }
        .audit-timeline {
          position: relative;
          padding-left: 30px;
        }
        .audit-timeline::before {
          content: '';
          position: absolute;
          left: 11px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #E4E6EF;
        }
        .audit-timeline-item {
          position: relative;
          padding-bottom: 24px;
        }
        .audit-timeline-item:last-child {
          padding-bottom: 0;
        }
        .audit-timeline-marker {
          position: absolute;
          left: -30px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .audit-timeline-content {
          background: #F8F9FA;
          border-radius: 8px;
          padding: 16px;
          margin-left: 8px;
        }
        .audit-timeline-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .audit-action {
          font-weight: 600;
          font-size: 0.95rem;
        }
        .audit-timestamp {
          font-size: 0.8rem;
          color: #B5B5C3;
        }
        .audit-details {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 8px;
        }
        .audit-detail-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
          color: #7E8299;
        }
        .audit-changes {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #E4E6EF;
        }
        .audit-change-block {
          margin-bottom: 8px;
        }
        .audit-change-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          display: block;
          margin-bottom: 4px;
        }
        .audit-change-block.old .audit-change-label {
          color: #F64E60;
        }
        .audit-change-block.new .audit-change-label {
          color: #1BC5BD;
        }
        .audit-change-block code {
          display: block;
          font-size: 0.8rem;
          background: #E4E6EF;
          padding: 8px;
          border-radius: 4px;
          overflow-x: auto;
          color: #3F4254;
        }
        .audit-integrity {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          margin-top: 8px;
          opacity: 0.8;
        }
        .audit-date-separator {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          color: #6C5CE7;
          padding: 8px 0 12px;
          margin-left: -30px;
        }
        .audit-date-separator::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #E4E6EF;
          margin-left: 8px;
        }
        .audit-search-wrapper {
          display: flex;
          align-items: center;
          position: relative;
          padding: 8px 0;
        }
        .audit-search-icon {
          position: absolute;
          left: 12px;
          color: #B5B5C3;
          pointer-events: none;
        }
        .audit-search-input {
          width: 100%;
          padding-left: 40px !important;
          padding-right: 36px !important;
        }
        .audit-search-clear {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #B5B5C3;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
        }
        .audit-search-clear:hover {
          color: #F64E60;
        }
        .audit-severity-badge {
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
